import { useTranslation } from "react-i18next";
import { Box, Container } from "@mui/material";
import ProductDataGrid from "../../components/dataGrid/ProductDataGrid"; //
import BasicTooltip from "../../components/tooltips/BasicTooltip";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { processingTypes } from "../../constants/searchpageTexts";
import { numberToStringFormat } from "../../components/board";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const MyPageProductDataArea = ({ greenBeansData }) => {
  const { t } = useTranslation();
  const formattedData = greenBeansData?.map((beanData, index) => ({
    id: index + 1,
    CoffeeKey: beanData.CoffeeKey, // 커피 키
    CoffeeName: beanData.CoffeeName, // 생두이름
    BeanVariety: beanData.BeanVariety, // 생두품종
    CountryNameEng: beanData.CountryNameEng, // 원산지
    ProductionYear: beanData.ProductionYear, // 생산년도
    Altitude: beanData.Altitude, // 고도
    StockDate: `${dayjs(beanData.StockDate).format("YYYY-MM-DD")}`, // 입고일자
    ProcessingCode: beanData.ProcessingCode, // 가공방식
    Company: beanData.Company, // 수입회사
    Weight: beanData.Weight, // 수입무게
    Farm: beanData.Farm, // 생산농장
  }));

  const columns = [
    {
      field: "id",
      headerName: "No.",
      flex: 0.1,
      type: 'number',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: "CoffeeKey",
      headerName: t('field.coffeekey'),
      flex: 1.5,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <BasicTooltip title={params.row.CoffeeKey} />,
      valueGetter: ({ value }) => value || "-",
    },
    {
      field: "CoffeeName",
      headerName: t('field.coffeename'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'left',
      renderCell: (params) => <BasicTooltip title={params.row.CoffeeName} />,
    },
    {
      field: "BeanVariety",
      headerName: t('field.beanvariety'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <BasicTooltip title={params.row.BeanVariety} />,
    },
    {
      field: "CountryNameEng",
      headerName: t('field.countrynameeng'),
      flex: 1.65,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <BasicTooltip title={params.row.CountryNameEng} />
      ),
    },
    {
      field: "Altitude",
      headerName: t('field.altitude'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <BasicTooltip title={params.row.Altitude} />,
    },
    {
      field: "ProcessingCode",
      headerName: t('field.processingcode'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const processingName =
          processingTypes[params.row.ProcessingCode] || "Unknown";
        return <BasicTooltip title={processingName} />;
      },
    },
    {
      field: "ProductionYear",
      headerName: t('field.productionyear'),
      flex: 1,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <BasicTooltip title={params.row.ProductionYear} />
      ),
    },
    {
      field: "Company",
      headerName: t('field.company'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <BasicTooltip title={params.row.Company} />,
    },
    {
      field: "StockDate",
      headerName: t('field.stockdate'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <BasicTooltip title={params.row.StockDate} />,
    },
    {
      field: "Weight",
      headerName: t('field.weight'),
      type: 'number',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'right',
      flex: 1,
      valueFormatter: (value) => numberToStringFormat(value),
    },
    {
      field: "Farm",
      headerName: t('field.farm'),
      flex: 1.25,
      type: 'string',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'left',
      renderCell: (params) => <BasicTooltip title={params.row.Farm} />,
    },
  ];

  const datagridSx = {
    border: "0",
    "& .MuiDataGrid-root": {
      border: "0px !important",
    },
    "& .MuiDataGrid-main": {},
    "& .MuiDataGrid-root--densityStandard": {},
    "& .MuiDataGrid-withBorderColor": {
      border: 0,
    },
    '& div[data-rowIndex][role="row"]:nth-of-type(5n-4)': {
      "& div": {},
    },
    "& .MuiDataGrid-virtualScrollerRenderZone": {
      "& .MuiDataGrid-row": {
        "&:nth-of-type(2n)": { backgroundColor: "rgba(238, 242, 245, 1)" },
        cursor: "pointer",
      },
    },
    "& .MuiDataGrid-cell:nth-of-type(7)": {
      "& .MuiDataGrid-cellContent": {
        borderBottom: "1px solid #000",
      },
    },
    "& .disabled-cell": {
      pointerEvents: "none",
      color: "rgba(0, 0, 0, 0.26)",
      "& .MuiDataGrid-cellContent": {
        borderBottom: "0 !important",
      },
    },
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
      outline: "none !important",
    },
    "& .MuiDataGrid-columnHeaders": {
      fontSize: 14,
      minHeight: "55px !important",
      maxHeight: "55px !important",
      lineHeight: "55px !important",
      borderTop: "#29314A 3px solid",
      borderBottom: "#29314A 3px solid",
      borderRadius: "0px !important",
    },
    "& .MuiDataGrid-footerContainer": {
      fontSize: 14,
      minHeight: "40px !important",
      maxHeight: "40px !important",
      lineHeight: "40px !important",
    },
  };

  return (
    <>
      <Box>
        <Container sx={{ p: "0 !important" }}>
          <Box>
            <ProductDataGrid
              rows={formattedData || []}
              columns={columns}
              getRowId={(row) => row.contract_sub_id}
              sx={datagridSx}
            />
          </Box>
        </Container>
      </Box>
    </>
  );
};
